import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import { Pressable, StyleSheet, Text, View } from "react-native";

import { useLiveTranslation } from "@/lib/live-translation/LiveTranslationProvider";
import { colors, fonts, radius, spacing } from "@/lib/theme";

interface Props {
    visitId: string;
    /** Patient's primary language as a free-text string from `patients.language`.
     *  If it loosely matches a supported language code or label we pre-select it. */
    patientLanguage?: string | null;
}

// English is intentionally absent — nurses always speak English, so a
// patient whose `patient.language` is "english" doesn't need translation.
// Returning null here means the modal will show the language picker
// rather than auto-starting with a pointless English↔English session.
//
// The agency portal's patient-language picker already distinguishes Mandarin
// from Cantonese, so "cantonese" maps to `yue`. Bare "chinese" stays Mandarin —
// it is genuinely ambiguous, and the nurse can switch in the picker.
const LANGUAGE_PREFILL_MAP: Record<string, string> = {
    spanish: "es",
    español: "es",
    russian: "ru",
    chinese: "zh",
    mandarin: "zh",
    putonghua: "zh",
    cantonese: "yue",
    yue: "yue",
    creole: "ht",
    "haitian creole": "ht",
    kreyol: "ht",
    bengali: "bn",
    bangla: "bn",
    portuguese: "pt",
    french: "fr",
    german: "de",
    japanese: "ja",
    korean: "ko",
    hindi: "hi",
    indonesian: "id",
    vietnamese: "vi",
    italian: "it"
};

function prefillFor(language: string | null | undefined): string | null {
    if (!language) {
        return null;
    }
    return LANGUAGE_PREFILL_MAP[language.trim().toLowerCase()] ?? null;
}

export function LiveTranslationButton({ visitId, patientLanguage }: Props) {
    const { open, isActive } = useLiveTranslation();
    const preselectedLanguage = prefillFor(patientLanguage);

    // Hide the entry point while a session is already running — the pill is
    // the only Live Translation UI in that state.
    if (isActive) {
        return null;
    }

    return (
        <Pressable
            onPress={() => open({ visitId, preselectedLanguage })}
            style={({ pressed }) => [styles.button, pressed && styles.pressed]}
            accessibilityRole="button"
            accessibilityLabel="Live Translation"
        >
            <MaterialCommunityIcons name="microphone" size={18} color={colors.info600} />
            <Text style={styles.label}>Live Translation</Text>
        </Pressable>
    );
}

const styles = StyleSheet.create({
    button: {
        // Center-aligned pill instead of full-width slab. The parent
        // ScrollView stretches its children edge-to-edge by default, so we
        // rely on alignSelf to size the pill to its content.
        alignSelf: "center",
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
        backgroundColor: colors.info50,
        borderRadius: radius.full,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.base,
        borderWidth: 1,
        borderColor: colors.info400
    },
    pressed: {
        opacity: 0.7
    },
    label: {
        fontFamily: fonts.semiBold,
        fontSize: 13,
        color: colors.info600
    }
});
